import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ExpenseBankSelectProps = {
  value: string;
  banks: BankOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

function itemValue(id: string): string {
  return `bank:${id}`;
}

export function ExpenseBankSelect({
  value,
  banks,
  onChange,
  placeholder = "Search bank",
  triggerClassName,
  disabled = false,
}: ExpenseBankSelectProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState("");

  const selected = useMemo(
    () =>
      banks.find(
        (bank) =>
          (bank.chartOfAccountsId != null && String(bank.chartOfAccountsId) === value) ||
          String(bank.id) === value ||
          bank.name.toLowerCase() === (value ?? "").toLowerCase(),
      ) ?? null,
    [banks, value],
  );

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-expense-bank-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setHighlight(value ? itemValue(value) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">
            {selected ? (selected.code ? `${selected.name} (${selected.code})` : selected.name) : placeholder}
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search bank..." className="h-9 text-xs" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              {banks.length === 0 ? "No banks found." : "No bank found."}
            </CommandEmpty>
            <CommandGroup>
              {banks.map((bank) => {
                const val = bank.chartOfAccountsId != null ? String(bank.chartOfAccountsId) : String(bank.id);
                const isSelected = val === value || String(bank.id) === value;
                return (
                  <CommandItem
                    key={bank.id}
                    value={itemValue(val)}
                    keywords={[bank.name, bank.shortName ?? "", bank.code ?? ""]}
                    data-expense-bank-selected={isSelected ? "true" : undefined}
                    onSelect={() => {
                      onChange(val);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="break-words font-medium truncate">
                      {bank.name} {bank.code ? <span className="opacity-75 font-normal shrink-0">({bank.code})</span> : null}
                    </span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
